import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

function EyeIcon({ open }) {
  return open ? (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function RegisterPage() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [step, setStep] = useState('form'); // 'form' | 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // OTP
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef([]);

  const pwRules = [
    { label: 'Mindestens 8 Zeichen', ok: password.length >= 8 },
    { label: 'Groß- und Kleinbuchstaben', ok: /[a-z]/.test(password) && /[A-Z]/.test(password) },
    { label: 'Mindestens eine Zahl', ok: /[0-9]/.test(password) },
  ];

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPw) { setError('Passwörter stimmen nicht überein.'); return; }
    if (password.length < 8) { setError('Passwort muss mindestens 8 Zeichen haben.'); return; }
    setLoading(true);
    const { error: err } = await signUp(email, password);
    setLoading(false);
    if (err) {
      setError(err.message === 'User already registered'
        ? 'Diese E-Mail ist bereits registriert.'
        : err.message);
    } else {
      setStep('otp');
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const token = otp.join('');
    if (token.length < 6) { setError('Bitte alle 6 Ziffern eingeben.'); return; }
    setError('');
    setLoading(true);
    const { error: err } = await supabase.auth.verifyOtp({ email, token, type: 'signup' });
    setLoading(false);
    if (err) {
      setError('Code falsch oder abgelaufen. Bitte erneut versuchen.');
    } else {
      navigate('/plan-select');
    }
  };

  const handleResend = async () => {
    setError('');
    await signUp(email, password);
    setOtp(['', '', '', '', '', '']);
    otpRefs.current[0]?.focus();
  };

  if (step === 'otp') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logo} onClick={() => navigate('/')}>
            <img src="/logo-frontup.png" style={{width:32,height:32,borderRadius:8,objectFit:'cover'}} alt="FrontUp" />
            <span style={styles.logoName}>FrontUp</span>
          </div>

          <div style={styles.mailIcon}>
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#fff" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>

          <h1 style={{...styles.title, marginTop: 20}}>Code eingeben</h1>
          <p style={styles.sub}>
            Wir haben einen 6-stelligen Code an <strong style={{color:'#111'}}>{email}</strong> gesendet.
          </p>

          <form onSubmit={handleVerify} style={styles.form}>
            <div style={styles.otpRow} onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { otpRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  style={{
                    ...styles.otpBox,
                    borderColor: digit ? '#111' : '#e5e5e5',
                  }}
                />
              ))}
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button type="submit" disabled={loading} style={styles.submitBtn}>
              {loading ? 'Prüfen...' : 'Bestätigen'}
            </button>
          </form>

          <p style={styles.switchText}>
            Keinen Code erhalten?{' '}
            <span style={styles.switchLink} onClick={handleResend}>Erneut senden</span>
          </p>
          <p style={{...styles.switchText, marginTop: 8}}>
            <span style={{...styles.switchLink, color:'#aaa', fontWeight:500}} onClick={() => setStep('form')}>← Zurück</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo} onClick={() => navigate('/')}>
          <img src="/logo-frontup.png" style={{width:32,height:32,borderRadius:8,objectFit:'cover'}} alt="FrontUp" />
          <span style={styles.logoName}>FrontUp</span>
        </div>

        <h1 style={styles.title}>Account erstellen</h1>
        <p style={styles.sub}>Starte kostenlos mit FrontUp.</p>

        <form onSubmit={handleRegister} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>E-Mail</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="deine@email.de"
              required
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Passwort</label>
            <div style={styles.pwWrap}>
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={{...styles.input, paddingRight: 44}}
              />
              <button type="button" onClick={() => setShowPw(v => !v)} style={styles.eyeBtn}>
                <EyeIcon open={showPw} />
              </button>
            </div>
            {password.length > 0 && (
              <div style={styles.pwRules}>
                {pwRules.map((r, i) => (
                  <div key={i} style={{...styles.pwRule, color: r.ok ? '#16a34a' : '#aaa'}}>
                    <CheckIcon /> {r.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Passwort bestätigen</label>
            <div style={styles.pwWrap}>
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPw}
                onChange={e => setConfirmPw(e.target.value)}
                placeholder="••••••••"
                required
                style={{...styles.input, paddingRight: 44}}
              />
              <button type="button" onClick={() => setShowConfirm(v => !v)} style={styles.eyeBtn}>
                <EyeIcon open={showConfirm} />
              </button>
            </div>
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" disabled={loading} style={styles.submitBtn}>
            {loading ? 'Registrieren...' : 'Kostenlos registrieren'}
          </button>
        </form>

        <p style={styles.switchText}>
          Bereits registriert?{' '}
          <span style={styles.switchLink} onClick={() => navigate('/login')}>Anmelden</span>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f7f7f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  card: {
    background: '#fff',
    border: '1.5px solid #e5e5e5',
    borderRadius: 20,
    padding: '48px 44px',
    width: '100%',
    maxWidth: 440,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 32,
    cursor: 'pointer',
  },
  logoName: {
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: '-.5px',
    color: '#111',
  },
  mailIcon: {
    width: 56,
    height: 56,
    background: '#111',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: 800,
    letterSpacing: '-1px',
    color: '#111',
    marginBottom: 6,
  },
  sub: {
    fontSize: 15,
    color: '#888',
    lineHeight: 1.6,
    marginBottom: 32,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: '#111',
  },
  input: {
    padding: '12px 14px',
    border: '1.5px solid #e5e5e5',
    borderRadius: 10,
    fontSize: 15,
    fontFamily: 'inherit',
    color: '#111',
    outline: 'none',
    width: '100%',
    background: '#fff',
    transition: 'border-color .15s',
  },
  pwWrap: {
    position: 'relative',
  },
  eyeBtn: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#aaa',
    display: 'flex',
    alignItems: 'center',
    padding: 0,
  },
  pwRules: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    marginTop: 4,
  },
  pwRule: {
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'color .2s',
  },
  otpRow: {
    display: 'flex',
    gap: 10,
    justifyContent: 'center',
  },
  otpBox: {
    width: 52,
    height: 60,
    border: '1.5px solid #e5e5e5',
    borderRadius: 12,
    fontSize: 24,
    fontWeight: 700,
    textAlign: 'center',
    color: '#111',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color .15s',
    background: '#fff',
  },
  error: {
    background: '#fff5f5',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    color: '#dc2626',
  },
  submitBtn: {
    padding: '14px',
    background: '#111',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    marginTop: 4,
    transition: 'background .15s',
  },
  switchText: {
    textAlign: 'center',
    fontSize: 14,
    color: '#888',
    marginTop: 24,
  },
  switchLink: {
    color: '#111',
    fontWeight: 700,
    cursor: 'pointer',
  },
};

export default RegisterPage;
